import { useState, useEffect } from 'react';
import { resumeApi, type Resume } from '@src/api/client';

const ResumesPage = () => {
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Resume | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    fullName: '',
    email: '',
    phone: '',
    location: '',
    summary: '',
    skills: '',
  });

  const load = async () => {
    try {
      const data = await resumeApi.list();
      setResumes(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setForm({ name: '', fullName: '', email: '', phone: '', location: '', summary: '', skills: '' });
    setEditing(null);
    setShowForm(false);
  };

  const startEdit = (r: Resume) => {
    setForm({
      name: r.name,
      fullName: r.contactInfo.fullName,
      email: r.contactInfo.email || '',
      phone: r.contactInfo.phone || '',
      location: r.contactInfo.location || '',
      summary: r.summary || '',
      skills: (r.skills || []).join(', '),
    });
    setEditing(r);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      const data: Partial<Resume> = {
        name: form.name,
        contactInfo: { fullName: form.fullName, email: form.email, phone: form.phone, location: form.location },
        summary: form.summary,
        skills: form.skills
          .split(',')
          .map(s => s.trim())
          .filter(Boolean),
      };

      if (editing) {
        await resumeApi.update(editing.id, data);
      } else {
        await resumeApi.create(data);
      }
      resetForm();
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save resume');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this resume?')) return;
    try {
      await resumeApi.delete(id);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete resume');
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await resumeApi.setDefault(id);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to set default');
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-20 rounded-xl bg-gray-200" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Resumes</h1>
          <p className="text-gray-500">Manage your base resumes</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="bg-brand-500 hover:bg-brand-600 rounded-lg px-4 py-2 text-sm font-medium text-white">
          Add Resume
        </button>
      </div>

      {showForm && (
        <div className="rounded-xl bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold">{editing ? 'Edit Resume' : 'New Resume'}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Resume Name</label>
              <input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="focus:border-brand-500 focus:ring-brand-500 mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1"
                placeholder="e.g. Software Engineer Resume"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Full Name</label>
              <input
                value={form.fullName}
                onChange={e => setForm({ ...form, fullName: e.target.value })}
                className="focus:border-brand-500 focus:ring-brand-500 mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                className="focus:border-brand-500 focus:ring-brand-500 mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Phone</label>
              <input
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                className="focus:border-brand-500 focus:ring-brand-500 mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Location</label>
              <input
                value={form.location}
                onChange={e => setForm({ ...form, location: e.target.value })}
                className="focus:border-brand-500 focus:ring-brand-500 mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Skills (comma-separated)</label>
              <input
                value={form.skills}
                onChange={e => setForm({ ...form, skills: e.target.value })}
                className="focus:border-brand-500 focus:ring-brand-500 mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1"
                placeholder="React, TypeScript, Go"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Summary</label>
              <textarea
                value={form.summary}
                onChange={e => setForm({ ...form, summary: e.target.value })}
                rows={3}
                className="focus:border-brand-500 focus:ring-brand-500 mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1"
              />
            </div>
          </div>
          {error && <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}
          <div className="mt-4 flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-brand-500 hover:bg-brand-600 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
              {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
            </button>
            <button
              onClick={resetForm}
              className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {resumes.length === 0 ? (
        <div className="rounded-xl bg-white p-12 text-center shadow-sm">
          <p className="text-gray-400">No resumes yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {resumes.map(r => (
            <div key={r.id} className="flex items-center justify-between rounded-xl bg-white p-5 shadow-sm">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium">{r.name}</h3>
                  {r.isDefault && (
                    <span className="bg-brand-100 text-brand-700 rounded-full px-2 py-0.5 text-xs font-medium">
                      Default
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  {r.contactInfo.fullName} {r.skills?.length ? `| ${r.skills.slice(0, 5).join(', ')}` : ''}
                </p>
                <p className="mt-0.5 text-xs text-gray-400">Updated {new Date(r.updatedAt).toLocaleDateString()}</p>
              </div>
              <div className="flex items-center gap-2">
                {!r.isDefault && (
                  <button
                    onClick={() => handleSetDefault(r.id)}
                    className="rounded-lg border px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50">
                    Set Default
                  </button>
                )}
                <button
                  onClick={() => startEdit(r)}
                  className="rounded-lg border px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50">
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(r.id)}
                  className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50">
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ResumesPage;
